import { useState, useEffect, useCallback } from 'react';
import * as Sentry from '@sentry/react';
import { message } from '@nula/core-ui';
import { commands } from '@/lib/platform/generated/bindings.gen';
import type { RecordingSettingsSnapshot } from '@/lib/platform/generated/bindings.gen';

// Re-export the canonical type from bindings so callers don't need to import
// from two places. The old local RecordingSettings type is replaced by the
// wire-accurate RecordingSettingsSnapshot.
export type RecordingSettings = RecordingSettingsSnapshot;

export type UseRecordingSettingsResult = {
  settings: RecordingSettings | null;
  loading: boolean;
  error: string | null;
  update: (patch: Partial<RecordingSettings>) => void;
  refresh: () => void;
};

export function useRecordingSettings(): UseRecordingSettingsResult {
  const [settings, setSettings] = useState<RecordingSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [fetchTick, setFetchTick] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    commands.getRecordingSettings()
      .then((res) => {
        if (cancelled) return;
        if (res.status === 'ok') {
          setSettings(res.data);
          setLoading(false);
        } else {
          setError(String(res.error));
          setLoading(false);
        }
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          Sentry.captureException(err);
          console.error('getRecordingSettings unexpected throw', err);
          setError(err instanceof Error ? err.message : String(err));
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [fetchTick]);

  const update = useCallback((patch: Partial<RecordingSettings>) => {
    // Snapshot current settings for rollback on error
    setSettings((prev) => {
      if (!prev) return prev;
      const optimistic = { ...prev, ...patch };
      // Fire-and-forget with Result branch handling
      commands.updateRecordingSettings({
        // Map Partial<RecordingSettings> → RecordingSettingsPatch.
        // The patch type uses | null to mean "no change"; undefined means same.
        // Note: audioRetentionDays is present on RecordingSettingsSnapshot (read)
        // but is NOT a field on RecordingSettingsPatch (write) — it is not user-
        // configurable via this command. Omit it from the patch object.
        calendarNotificationsEnabled: patch.calendarNotificationsEnabled ?? null,
        appDetectionEnabled: patch.appDetectionEnabled ?? null,
        shortcut: patch.shortcut ?? null,
        micDeviceId: patch.micDeviceId,
        systemAudioDeviceId: patch.systemAudioDeviceId,
        browserDetectionEnabled: patch.browserDetectionEnabled ?? null,
        autoJoinEnabled: patch.autoJoinEnabled ?? null,
        autoEndEnabled: patch.autoEndEnabled ?? null,
        endGraceSecs: patch.endGraceSecs !== undefined ? patch.endGraceSecs : null,
      }).then((res) => {
        if (res.status === 'error') {
          // Surface error and rollback optimistic update
          Sentry.captureMessage(`update_recording_settings failed: ${String(res.error)}`);
          console.error('update_recording_settings failed', res.error);
          message.error('Failed to save recording settings');
          setFetchTick((t) => t + 1); // trigger re-fetch for rollback
        }
        // status === 'ok': persisted successfully, optimistic state is correct.
      });
      return optimistic;
    });
  }, []);

  const refresh = useCallback(() => {
    setFetchTick((t) => t + 1);
  }, []);

  return { settings, loading, error, update, refresh };
}
